import React from 'react';

import { logError } from '@helpers/Loggers';
import type { MapRegion } from '@helpers/Map';

/** Max plans returned by each Explorer Firestore query chunk. */
export const DEFAULT_EXPLORER_REGION_QUERY_LIMIT = 20;
export const EXPLORER_MAP_BOUNDS_QUERY_DEBOUNCE_MS = 2000;

/**
 * Debounces user-driven map region changes before Explorer queries more region data.
 * Programmatic map fitting should not call this hook; only gestures should schedule reloads.
 */
export function useDebouncedMapRegionChange(
  onMapRegionChange: (region: MapRegion) => void | Promise<void>,
  delayMs = EXPLORER_MAP_BOUNDS_QUERY_DEBOUNCE_MS
) {
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onMapRegionChangeRef = React.useRef(onMapRegionChange);

  React.useEffect(() => {
    onMapRegionChangeRef.current = onMapRegionChange;
  }, [onMapRegionChange]);

  React.useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  return React.useCallback(
    (region: MapRegion) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        Promise.resolve(onMapRegionChangeRef.current(region)).catch((error) => logError(error));
      }, delayMs);
    },
    [delayMs]
  );
}
